import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Home } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = 'Privacy Policy - AUMONLINE Financial Planner';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-8">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2 border-2 border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </Button>
            
            <div className="flex flex-col items-center gap-4">
              <a href="https://www.aumonline.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/5j61poys_Logo.jpg" 
                  alt="AUMONLINE Logo" 
                  className="h-16 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
            </div>
            
            <div className="w-32"></div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Updated at December 15th, 2025</p>

          <div className="prose max-w-none space-y-6 text-gray-700">
            <p>
              AUMONLINE Financial planner ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed by AUMONLINE Financial planner. This Privacy Policy applies to our website, and its associated subdomains (collectively, our "Service") alongside our application, AUMONLINE Financial planner. By accessing or using our Service, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your personal information as described in this Privacy Policy and our Terms of Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Definitions and Key Terms</h2>
            <p>To help explain things as clearly as possible in this Privacy Policy, every time any of these terms are referenced, are strictly defined as:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Cookie:</strong> small amount of data generated by a website and saved by your web browser. It is used to identify your browser, provide analytics, remember information about you such as your language preference or login information.</li>
              <li><strong>Company:</strong> when this policy mentions "Company," "we," "us," or "our," it refers to Aumonline, (B-401, Vaishnavi Sarovar, Yadavgiri, Mysore, India - 570020), that is responsible for your information under this Privacy Policy.</li>
              <li><strong>Country:</strong> where AUMONLINE Financial planner or the owners/founders of AUMONLINE Financial planner are based, in this case is India</li>
              <li><strong>Customer:</strong> refers to the company, organization or person that signs up to use the AUMONLINE Financial planner Service to manage the relationships with your consumers or service users.</li>
              <li><strong>Device:</strong> any internet connected device such as a phone, tablet, computer or any other device that can be used to visit AUMONLINE Financial planner and use the services.</li>
              <li><strong>Personal Data:</strong> any information that directly, indirectly, or in connection with other information — including a personal identification number — allows for the identification or identifiability of a natural person.</li>
              <li><strong>Service:</strong> refers to the service provided by AUMONLINE Financial planner as described in the relative terms (if available) and on this platform.</li>
              <li><strong>Website:</strong> AUMONLINE Financial planner's site, which can be accessed via this URL: https://planner.aumonline.in/</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">What Information Do We Collect?</h2>
            <p>We collect information from you when you visit our website, register on our site, place an order, subscribe to our newsletter, respond to a survey or fill out a form.</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Name</li>
              <li>Username</li>
              <li>Phone Numbers</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Do We Share Information with Third Parties?</h2>
            <p>
              We may share the information that we collect, both personal and non-personal, with third parties such as advertisers, contest sponsors, promotional and marketing partners, and others who provide our content or whose products or services we think may interest you. We may also share it with our current and future affiliated companies and business partners.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">How Do We Use The Information We Collect?</h2>
            <p>Any of the information we collect from you may be used in one of the following ways:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To personalize your experience (your information helps us to better respond to your individual needs)</li>
              <li>To improve our website (we continually strive to improve our website offerings based on the information and feedback we receive from you)</li>
              <li>To improve customer service (your information helps us to more effectively respond to your customer service requests and support needs)</li>
              <li>To process transactions</li>
              <li>To administer a contest, promotion, survey or other site feature</li>
              <li>To send periodic emails</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">How Do We Protect Your Information?</h2>
            <p>
              We implement a variety of security measures to maintain the safety of your personal information when you place an order or enter, submit, or access your personal information. We offer the use of a secure server. All supplied sensitive/credit information is transmitted via Secure Socket Layer (SSL) technology and then encrypted into our Payment gateway providers database.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Cookies</h2>
            <p>
              AUMONLINE Financial planner uses "Cookies" to identify the areas of our website that you have visited. A Cookie is a small piece of data stored on your computer or mobile device by your web browser. We use Cookies to enhance the performance and functionality of our website but are non-essential to their use.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Kids' Privacy</h2>
            <p>
              We do not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">GDPR Compliance</h2>
            <p>
              We may be collecting and using information from you if you are from the European Economic Area (EEA). GDPR is an EU-wide privacy and data protection law that regulates how EU residents' data is protected by companies and enhances the control the EU residents have over their personal data.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Changes To Our Privacy Policy</h2>
            <p>
              We may change our Service and policies, and we may need to make changes to this Privacy Policy so that they accurately reflect our Service and policies. Unless otherwise required by law, we will notify you (for example, through our Service) before we make changes to this Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Contact Us</h2>
            <p>Don't hesitate to contact us if you have any questions:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Via Email: <a href="mailto:ashwath@aumonline.in" className="text-blue-600 hover:underline">ashwath@aumonline.in</a></li>
              <li>Via Phone: <a href="tel:+918217589700" className="text-blue-600 hover:underline">+918217589700</a></li>
              <li>Via Website: <a href="https://planner.aumonline.in" className="text-blue-600 hover:underline">https://planner.aumonline.in</a></li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-600">
          <p className="text-sm">
            © 2025 AUMONLINE. All rights reserved. | 
            <button onClick={() => navigate('/terms')} className="text-blue-600 hover:underline ml-2">Terms of Service</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
